<template>
  <section class="section">
    <BaseForm
      ref="baseForm"
      :title="$t('models.auth.accountActivation')"
      :data-object="activateProfileRequest"
      :content-type="'verify-email'"
      :custom-submit="true"
      :on-custom-submit="enterKey"
      :has-error="!passwordsMatch"
      :error-message="errorMessage"
      :can-save="canSave"
      :column-width="'is-one-third is-offset-4'"
    >
      <template #content>
        <v-text-field
          v-model.trim="activateProfileRequest.username"
          label="Benutzername"
          :disabled="activation"
          class="field"
          name="username"
          value=""
          required
          color="primary"
        />

        <v-text-field
          v-if="!activation"
          v-model="activateProfileRequest.password1"
          :disabled="activation"
          :label="$t('models.auth.newPassword')"
          class="field"
          name="password1"
          type="password"
          value=""
          color="primary"
          required
          password-reveal
          placeholder=""
        />

        <v-text-field
          v-if="!activation"
          v-model.trim="activateProfileRequest.password2"
          :label="$t('models.auth.repeatPassword')"
          class="field"
          color="primary"
          name="password2"
          type="password"
          value=""
          required
          password-reveal
          @keyup.enter="enterKey"
        />

        <v-alert v-if="!activation" :closable="false" type="info">
          {{ $t('models.auth.emailPinInfo') }}
        </v-alert>

        <v-text-field
          v-if="activation"
          v-model.trim="profileActivation.pin"
          :label="$t('models.auth.pinFromEmail')"
          class="field"
          name="ping"
          value=""
          required
          @keyup.enter="enterKey"
        />

        <div v-if="activation" class="field">
          <v-checkbox v-model="agbAccepted">
            Mit der Accountaktivierung erklären Sie sich mit den Allgemeinen
            Geschäfts- und Lieferbedingungen (AGB), online unter
            <a href="https://www.brelag.com/agb" target="_blank"
              >www.brelag.com/agb</a
            >, sowie mit der BRELAG Datenschutzerklärung, online unter
            <a
              href="https://www.brelag.com/datenschutzbestimmungen"
              target="_blank"
              >www.brelag.com/datenschutzbestimmungen</a
            >, einverstanden.
          </v-checkbox>
        </div>
      </template>
    </BaseForm>
  </section>
  <LoadingOverlay v-model:loading="loading" />
</template>

<script setup lang="ts">
import axios from 'axios'
import { computed, onMounted, Ref, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { apiClient } from '@/api/ApiClient'
import BaseForm from '@/components/common/BaseForm.vue'
import LoadingOverlay from '@/components/common/LoadingOverlay.vue'
import { i18n } from '@/lang/setup'
import {
  ProfileActivation,
  ProfileActivationRequest,
} from '@/models/core/models'

const route = useRoute()
const router = useRouter()
const store = useStore()
const loading = ref(false)

const activateProfileRequest: Ref<ProfileActivationRequest> = ref({
  username: (route.query.username as string) || '',
  email: (route.query.email as string) || '',
  password1: '',
  password2: '',
})
const profileActivation = ref({
  id: '',
  nonce: '',
  pin: '',
})
const errorMessage = ref('')
const agbAccepted = ref(false)

const baseForm = ref(null)

const requestActivateProfile = async () => {
  loading.value = true
  activateProfileRequest.value.email = activateProfileRequest.value.username
  try {
    const response = await apiClient.requestActivateProfile(
      activateProfileRequest.value
    )
    baseForm.value.clearError()
    profileActivation.value.nonce = response.data.nonce
    profileActivation.value.id = response.data.profile
  } catch (error) {
    baseForm.value.handleError(error)
  }
  loading.value = false
}

const activateProfile = async () => {
  loading.value = true
  try {
    const response = await apiClient.activateProfile(profileActivation.value)
    router.push({ name: 'login' })
  } catch (error) {
    profileActivation.value.nonce = ''
    profileActivation.value.pin = ''
    baseForm.value.handleError(error)
  }
  loading.value = false
}

const enterKey = () => {
  if (!activation.value) {
    requestActivateProfile()
  } else {
    activateProfile()
  }
}

onMounted(() => {
  axios
    .post(
      '/api/v1/auth/logout',
      {
        user: {
          id: store.state.global.profileId,
          is_authenticated: store.state.global.is_authenticated,
        },
      },
      store.state.global.axiosConfig
    )
    .then((response) => {
      store.dispatch({
        type: 'global/logoutUser',
      })
    })
    .catch((error) => {
      console.warn(error)
    })
})

const passwordsMatch = computed(() => {
  if (
    activateProfileRequest.value.password1 ===
    activateProfileRequest.value.password2
  ) {
    return true
  } else {
    if (
      activateProfileRequest.value.password1 !== '' &&
      activateProfileRequest.value.password2 !== ''
    ) {
      errorMessage.value = i18n.global.t('models.auth.passwordNoMatch')
      return false
    } else {
      return true
    }
  }
})

const activation = computed(() => {
  return profileActivation.value.nonce !== ''
})

const canSave = computed((): boolean => {
  if (activation.value) {
    return agbAccepted.value
  } else {
    return passwordsMatch.value
  }
})
</script>

<style lang="scss" scoped></style>
